
import { Component, Vue, Watch } from 'vue-property-decorator';
import { Action, Getter } from 'vuex-class';
import Toast from '@/utils/toast';
import SuitesHeader from '@/components/shared/suites/SuitesHeader.vue';
import FullPageSpinner from '@/components/shared/suites/FullPageSpinner.vue';
import SubsidyCodes from '@/components/subsidies/SubsidyCodes.vue';
import SubsidyConfiguration from '@/components/subsidies/SubsidyConfiguration.vue';

const namespace: string = 'subsidies';

@Component<Subsidies>({
	components: {
		SuitesHeader,
		FullPageSpinner,
		SubsidyCodes,
		SubsidyConfiguration
	}
})
export default class Subsidies extends Vue {
	@Action('fetchSubsidies', { namespace }) private fetchSubsidies!: () => Promise<void>;
	@Getter('getSubsidies', { namespace }) private subsidies!: Subsidy[];
	@Getter('hasSubsidies', { namespace: 'auth' }) private hasSubsidies!: boolean;

	/**
	 * Re-sync the local subsidy copy whenever the store changes. Vuetify keeps a tab item mounted once
	 * it has been visited, so `created` won't run again to pick up changes made from another tab — such as
	 * clearing students & subsidies in Business Settings. Skipped while the user is configuring a subsidy
	 * so an unrelated store refresh can't discard their unsaved edits.
	 *
	 * @return {void}
	 */
	@Watch('subsidies')
	private onSubsidiesChange(): void {
		if (!this.isConfiguringSubsidy && this.subsidies?.length) {
			this.setSelectedSubsidy();
		}
	}

	// Data
	private isDataFetching: boolean = false;
	private isConfiguringSubsidy: boolean = false;
	private subsidy: Subsidy = {
		type: undefined,
		is_active: true,
		target_users: 'codes',
		target_items: 'meals',
		codes: undefined
	};


	private get shouldShowSubsidyCodes(): boolean {
		return !!(this.subsidy.id && (
			this.subsidy.target_users === 'codes' ||
			(this.subsidy.codes && this.subsidy.codes.length)
		));
	}


	/**
	 * Fetch existing subsidies
	 * If there are existing subsidies, load the first active one. We currently support only one subsidy per location.
	 * If there are no subsidies, set the subsidy to be configured.
	 *
	 * @return {Promise<void>}
	 */
	private async created(): Promise<void> {
		this.isDataFetching = true;

		if(!this.hasSubsidies) {
			this.$router.replace('/menus');
		}

		try {
			await this.fetchSubsidies();
			if (this.subsidies?.length) {
				this.setSelectedSubsidy();
			}
			else {
				this.isConfiguringSubsidy = true;
				this.onSubsidyTypeChange('discount');
			}
		}
		catch (errorMessage) {
			Toast.error(this.$t('settings.subsidies.error_fetching', { errorMessage }));
		}
		finally {
			this.isDataFetching = false;
		}
	}

	private setSelectedSubsidy(): void {
		this.subsidy = { ...this.subsidies[0] };
	}

	/**
	 * When subsidy type changes, reset the subsidy object
	 * depending on the new type.
	 *
	 * @param {BaseSubsidy['type']} newType
	 * @return {void}
	 */
	private onSubsidyTypeChange(newType: BaseSubsidy['type']): void {
		const baseSubsidy: BaseSubsidy = {
			id: this.subsidy?.id || undefined,
			type: newType,
			target_users: 'codes',
			is_active: true,
			codes: this.subsidy?.codes || undefined,
			target_items: 'meals'
		};

		switch (newType) {
			case 'discount':
				this.subsidy = {
					...baseSubsidy,
					discount_amount: null,
					discount_percentage: null
				} as DiscountSubsidy;
				break;
			case 'fixed_price':
				this.subsidy = {
					...baseSubsidy,
					discount_fixed_price: null
				} as FixedPriceSubsidy;
				break;
			default:
				this.subsidy = {
					...baseSubsidy
				} as PayWhatYouCanSubsidy;
		}

		this.resetValidator();
	}


	/**
	 * Cancels the subsidy configuration.
	 * If there are existing subsidies, it resets the subsidy to the existing data.
	 *
	 * @return {void}
	 */
	private cancelConfiguration(): void {
		if(this.subsidies?.length) {
			this.setSelectedSubsidy();
		}

		this.isConfiguringSubsidy = false;
	}

	/**
	 * Handle error messages from child components
	 *
	 * @param {string} message
	 * @return {void}
	 */
	private onShowError(message: string): void {
		Toast.error(message);
	}

	/**
	 * Resets the validation observer in SubsidyConfiguration
	 * This is to avoid validation errors showing up when switching subsidy types or switching
	 * between input types.
	 *
	 * @return {void}
	 */
	private resetValidator(): void {
		const subsidyConfig = this.$refs.subsidyConfiguration as any;
		if (subsidyConfig) {
			subsidyConfig.resetValidator();
		}
	}

	/**
	 * Handle subsidy saved event from SubsidyConfiguration
	 *
	 * @return {Promise<void>}
	 */
	private async onSubsidySaved(): Promise<void> {
		setTimeout(async () => {
			// Refetch subsidies to update the store after save. We add a small delay in order to ensure the backend is up-to-date.
			await this.fetchSubsidies();
			if (this.subsidies?.length) {
				this.setSelectedSubsidy();
			}
			this.isConfiguringSubsidy = false;
		}, 400);
	}

	/**
	 * Handle subsidy deleted event from SubsidyConfiguration
	 *
	 * @return {Promise<void>}
	 */
	private async onSubsidyDeleted(): Promise<void> {
		await this.fetchSubsidies();

		// Reset subsidy to initial state
		this.subsidy = {
			type: undefined,
			is_active: true,
			target_users: 'codes',
			target_items: 'meals',
			codes: undefined
		};

		this.isConfiguringSubsidy = true;
	}

	/**
	 * Handles the refetch-subsidies event from SubsidyCodes component.
	 * Refetches subsidies and updates the current subsidy.
	 *
	 * @return {Promise<void>}
	 */
	private async onRefetchSubsidies(): Promise<void> {
		try {
			await this.fetchSubsidies();
			if (this.subsidies?.length) {
				this.setSelectedSubsidy();
			}
		}
		catch (errorMessage) {
			Toast.error(this.$t('settings.subsidies.error_fetching', { errorMessage }));
		}
	}
}
